/** @jsx jsx */
import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import throttle from 'lodash/throttle';
// eslint-disable-next-line jira/restricted/@atlaskit+width-detector
import { WidthObserver } from '@atlaskit/width-detector';
import { useSsrPlaceholders } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import { ConsentBanner } from '@atlassian/browser-storage-controls';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { MIN_BANNER_WIDTH } from '../../constants.tsx';
import {
	useCookieConsentBannerSettings,
	useCookieConsentBannerSettingsActions,
} from '../../services/preferences/index.tsx';

export const CookieConsentBanner = () => {
	const displaySsrPlaceholderAttrs = useSsrPlaceholders();

	const [{ height }] = useCookieConsentBannerSettings();
	const { onDismiss, setHeight } = useCookieConsentBannerSettingsActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const ref = useRef<HTMLDivElement>(null);

	// START - Analytics
	// Fire analytics if the banner height changes (and not due to a resize)
	const previousHeight = useRef<number>(height);
	const hasFiredAnalytics = useRef<boolean>(false);
	const hasResized = useRef<boolean>(false);

	useEffect(() => {
		if (fg('killswitch-analytics-ssr-cookie-consent-banner')) {
			return;
		}

		if (!hasResized.current && height !== previousHeight.current && !hasFiredAnalytics.current) {
			// Fire analytics event when the banner height changes (and not from a resize) so we know how often it happens
			// Eg because the default banner height differs from the actual banner height
			// This can cause a layout shift if the banner height is not set correctly and thus impact VC90
			// However, this may only be an edge case, so we want to verify how often it happens with these analytics before we invest effort into fixing it

			fireOperationalAnalytics(createAnalyticsEvent({}), 'cookieConsentBanner heightChanged', {
				height,
				previousHeight: previousHeight.current,
			});
			hasFiredAnalytics.current = true;
			previousHeight.current = height;
		}
	}, [createAnalyticsEvent, height]);

	useEffect(() => {
		if (fg('killswitch-analytics-ssr-cookie-consent-banner')) {
			return;
		}

		const onResize = () => {
			hasResized.current = true;
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('resize', onResize);
		};
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('resize', onResize);
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('resize', onResize);
		};
	});
	// END - Analytics

	// banner height is set either due to screen width changes or consent banner finishes rendering
	const setBannerHeight = useCallback(() => {
		ref.current && setHeight(ref.current.clientHeight);
	}, [setHeight]);

	// throttled banner height setter is used for dynamic banner height when screen width changes
	const throttledSetBannerHeight = useMemo(() => throttle(setBannerHeight, 50), [setBannerHeight]);

	useEffect(() => {
		const action = 'rendered';
		const actionSubject = 'cookieConsentBanner';
		const event = createAnalyticsEvent({
			action,
			actionSubject,
		});
		fireUIAnalytics(event, `${actionSubject} ${action}`);
	}, [createAnalyticsEvent]);

	const onConsentBannerBailOut = useCallback(() => {
		const actionSubject = 'consentBanner';
		const action = 'bailedOut';
		const event = createAnalyticsEvent({
			action,
			actionSubject,
		});
		fireTrackAnalytics(event, `${actionSubject} ${action}`);

		// consider banner as dismissed if it reports a bail out
		onDismiss();
	}, [onDismiss, createAnalyticsEvent]);

	return (
		<>
			<WidthObserver setWidth={throttledSetBannerHeight} />
			<div
				data-ssr-placeholder={
					__SERVER__ && displaySsrPlaceholderAttrs ? 'cookie-consent-banner' : undefined
				}
				data-ssr-placeholder-replace={
					__SERVER__ || !displaySsrPlaceholderAttrs ? undefined : 'cookie-consent-banner'
				}
				ref={ref}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				css={css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					...(!height && {
						visibility: 'hidden',
					}),
				})}
			>
				<ConsentBanner
					// If you pass localConsentMode=true here you will need to change the logic in
					// jira/src/packages/platform/ui/banners/cookie-consent/src/controllers/index.tsx shouldRenderBanner
					// as it expects that localConsentMode is false
					onHide={onDismiss}
					onShow={setBannerHeight}
					minWidth={MIN_BANNER_WIDTH}
					onConsentBannerBailOut={onConsentBannerBailOut}
					product="Jira"
				/>
			</div>
		</>
	);
};
